import { createSelector } from 'reselect';
import { get, find, sumBy, omit } from 'lodash';
import { makeSelectUserType } from 'containers/User/selectors';
import { makeSelectSurveyType } from 'containers/Survey/selectors';
import { SURVEY_TYPE_PEEQ } from 'containers/Survey/constants';

import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
/**
 * Direct selector to the TargetingPage state domain
 */
const selectTargetingPageDomain = (state) => get(state, 'targetingPage', {});
const selectAudiencesDomain = (state) =>
  get(state, 'targetingPage.audiences', []);
const selectActiveAudienceRefIdDomain = (state) =>
  get(state, 'targetingPage.activeAudienceRefId');
/**
 * Other specific selectors
 */

/**
 * Default selector used by TargetingPage
 */
const getAudienceCriteriaByRefId = (audiences, audienceRefId) =>
  get(find(audiences, ['refId', audienceRefId]), 'criteria', []);

const withAudienceCriteria =
  (criteriaSelector) =>
  (audiences, audienceRefId, ...restArgs) =>
    criteriaSelector(
      getAudienceCriteriaByRefId(audiences, audienceRefId),
      ...restArgs,
    );

const makeSelectActiveAudienceRefId = createSelector(
  selectActiveAudienceRefIdDomain,
  (substate) => substate,
);

const makeSelectAudiences = createSelector(
  selectAudiencesDomain,
  (substate) => substate,
);

const makeSelectActiveAudience = createSelector(
  selectAudiencesDomain,
  selectActiveAudienceRefIdDomain,
  (audience, audienceRefId) => find(audience, ['refId', audienceRefId]),
);

const makeSelectCriteria = createSelector(
  selectAudiencesDomain,
  selectActiveAudienceRefIdDomain,
  withAudienceCriteria((criteria) =>
    criteria.sort(({ order: orderA }, { order: orderB }) => orderA - orderB),
  ),
);

const makeSelectCriteriaByUserPermissions = createSelector(
  selectAudiencesDomain,
  selectActiveAudienceRefIdDomain,
  makeSelectUserType,
  makeSelectSurveyType,
  withAudienceCriteria((criteria, userType, surveyType) =>
    surveyType === SURVEY_TYPE_PEEQ
      ? []
      : criteria.filter(
          (criterion) =>
            criterion.isEnabled &&
            criterion.allowedUserTypes.includes(userType) &&
            criterion.allowedSurveyTypes.includes(surveyType),
        ),
  ),
);

const makeSelectAllAudienceCriteriaByUserPermissions = createSelector(
  selectAudiencesDomain,
  makeSelectUserType,
  makeSelectSurveyType,
  (audiences = [], userType, surveyType) =>
    audiences.map(({ criteria = [], ...audienceProps }) => ({
      ...audienceProps,
      criteria:
        surveyType === SURVEY_TYPE_PEEQ
          ? []
          : criteria.filter(
              (criterion) =>
                criterion.isEnabled &&
                criterion.allowedUserTypes.includes(userType) &&
                criterion.allowedSurveyTypes.includes(surveyType),
            ),
    })),
);

const makeSelectAllAudienceCriteriaValuesPerKey = (criterionKey) =>
  createSelector(
    makeSelectAllAudienceCriteriaByUserPermissions,
    (audiences = []) => {
      const allCriteria = audiences.reduce(
        (accumulator, { criteria }) => [...accumulator, ...criteria],
        [],
      );

      return allCriteria.reduce((accumulator, { key, values }) => {
        if (key === criterionKey && !!values) {
          return [...accumulator, ...values.map(({ value }) => value)];
        }
        return accumulator;
      }, []);
    },
  );

const makeSelectSelectedCriteria = createSelector(
  selectAudiencesDomain,
  selectActiveAudienceRefIdDomain,
  withAudienceCriteria((criteria) =>
    criteria.filter((criterion) => criterion.isEnabled),
  ),
);

const makeSelectSelectedCriteriaPerAudience = createSelector(
  selectAudiencesDomain,
  makeSelectSurveyType,
  (audiences = [], surveyType) =>
    surveyType === SURVEY_TYPE_PEEQ
      ? []
      : audiences.map((audience) => ({
          ...omit(audience, ['quota']),
          criteria: audience.criteria.filter(
            (criterion) => criterion.isEnabled,
          ),
        })),
);

const makeSelectTargetingVersion = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.targetingEditVersion,
);
const makeSelectInitialTargetingVersion = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.startVersion,
);
const makeSelectSavedTargetingVersion = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.savedTargetingVersion,
);

const makeSelectPreviousStatusName = createSelector(
  selectTargetingPageDomain,
  (substate) => get(substate, 'previousStatusName', undefined),
);

const makeSelectTargetingPage = createSelector(
  selectTargetingPageDomain,
  (substate) => substate,
);

const makeSelectScreeningQuestions = createSelector(
  selectTargetingPageDomain,
  (substate) =>
    get(substate, 'screeningQuestions', []).map((question) => {
      const { hasQuotas, answers = [] } = question;
      const hasScreeningAnswer = answers.some(
        ({ screenerType }) => screenerType !== SCREENING_ALLOWED_VALUES.ALLOWED,
      );

      return {
        ...question,
        isScreenerActive: hasQuotas || hasScreeningAnswer,
      };
    }),
);

const makeSelectErrors = createSelector(selectTargetingPageDomain, (substate) =>
  get(substate, 'errors', []),
);

const makeSelectIsTargetingSaving = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.isSaving,
);
const makeSelectIsTargetingFetching = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.isFetching,
);

const makeSelectAudienceErrors = createSelector(
  selectTargetingPageDomain,
  (substate) =>
    get(substate, 'errors', []).filter(
      (error) => error.category === 'audience' || error.category === 'criteria',
    ),
);

const makeSelectAudienceCompletesSum = createSelector(
  selectAudiencesDomain,
  (audiences = []) => sumBy(audiences, 'quotaCompletes'),
);
const makeSelectHasFetched = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.hasFetched,
);

const makeSelectHasPendingChanges = createSelector(
  makeSelectTargetingVersion,
  makeSelectSavedTargetingVersion,
  makeSelectIsTargetingSaving,
  (editVersion = -1, savedVersion = -1, isSaving) =>
    editVersion !== savedVersion || isSaving,
);

export default makeSelectTargetingPage;
export {
  selectTargetingPageDomain,
  makeSelectTargetingVersion,
  makeSelectInitialTargetingVersion,
  makeSelectPreviousStatusName,
  makeSelectCriteria,
  makeSelectSelectedCriteria,
  makeSelectSelectedCriteriaPerAudience,
  makeSelectScreeningQuestions,
  makeSelectErrors,
  makeSelectIsTargetingSaving,
  makeSelectCriteriaByUserPermissions,
  makeSelectAllAudienceCriteriaByUserPermissions,
  makeSelectAllAudienceCriteriaValuesPerKey,
  makeSelectAudiences,
  makeSelectActiveAudience,
  makeSelectActiveAudienceRefId,
  makeSelectAudienceErrors,
  makeSelectAudienceCompletesSum,
  makeSelectIsTargetingFetching,
  makeSelectHasFetched,
  makeSelectHasPendingChanges,
};
